import axios from "axios";
import { API_ROOT } from "./utils";

export type GetAllContestsResponse = {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  contest_name: string;
  contest_banner: string;
  telegram_media: string;
  telegram_media_type: string;
  start_time: Date;
  end_time: Date;
  users_count: string;
  votes_count: string;
  activities_count: string;
  is_expired: false;
  run_time_hours: string;
  status: string;
  description: string;
  currency: string;
  total_prize: number | null;
  first_prize: number | null;
  second_prize: number | null;
  third_prize: string | null;
  other_prize: string | null;
  chat_title: string;
  chat_username: string;
  logo_url: string;
  website: string;
  telegram: string;
  contract: string;
  chain: string;
  twitter: string;
  symbol: string;
  created_on: Date;
}[];

export type ContestActivity = {
  id: string;
  contest_id: string;
  user_id: string;
  content_link: string;
  content_type: string;
  file_name: string;
  created_on: any;
};

export interface ContestVote {
  activity_id: string;
  user_id: string;
  created_on: any;
}

export type ContestStatusType = "created" | "pending" | "paused" | "completed";

type GetAllContestsFilters = {
  statuses?: ContestStatusType[];
  projectIds?: string[];
  contestIds?: string[];
  pagination?: {
    offset: number;
    limit: number;
    orderBy?: string;
    orderDirection?: "asc" | "desc";
  };
};

export const getAllContests = async (filters?: GetAllContestsFilters) => {
  try {
    const params = new URLSearchParams();

    if (filters?.statuses) {
      filters.statuses.forEach((status) => params.append("statuses[]", status));
    }
    if (filters?.projectIds) {
      filters.projectIds.forEach((id) => params.append("projectIds[]", id));
    }
    if (filters?.contestIds) {
      filters.contestIds.forEach((id) => params.append("contestIds[]", id));
    }
    if (filters?.pagination) {
      params.append("offset", filters.pagination.offset.toString());
      params.append("limit", filters.pagination.limit.toString());
      if (filters.pagination.orderBy) {
        params.append("orderBy", filters.pagination.orderBy);
      }
      if (filters.pagination.orderDirection) {
        params.append("orderDirection", filters.pagination.orderDirection);
      }
    }

    const response = await axios.get<{
      contests: GetAllContestsResponse;
      totalCount: number;
    }>(`${API_ROOT}/api/contests/?${params.toString()}`);
    return response.data || {};
  } catch (error) {
    throw error;
  }
};

export const createContestActivity = async (payload: FormData) => {
  try {
    const res = await axios.post(
      `${API_ROOT}/api/contest-activities`,
      payload,
      {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      }
    );
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const getAllContestActivities = async (
  contestId: string,
  limit = 100,
  offset = 0
) => {
  try {
    if (!contestId) {
      return {} as {
        activities: ContestActivity[];
        totalCount: number;
      };
    }
    const response = await axios.get<{
      activities: ContestActivity[];
      totalCount: number;
    }>(
      `${API_ROOT}/api/contest-activities/${contestId}?limit=${limit}&offset=${offset}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteContestActivityById = async (
  contestId: string,
  activityId: string
) => {
  try {
    if (!(activityId && contestId)) {
      return;
    }
    await axios.delete(
      `${API_ROOT}/api/contest-activities/${contestId}/activity/${activityId}`
    );
  } catch (e) {
    throw e;
  }
};

export const createContestActivityVoteForUser = async (payload: {
  activityId: string;
  userId: string;
}) => {
  try {
    const res = await axios.post(`${API_ROOT}/api/contest-votes/`, payload);
    return res.data;
  } catch (e) {
    throw e;
  }
};

export const deleteContestActivityVoteForUser = async (payload: {
  activityId: string;
  userId: string;
}) => {
  try {
    if (!(payload.activityId && payload.userId)) {
      return;
    }
    await axios.delete(
      `${API_ROOT}/api/contest-votes/activities/${payload.activityId}/users/${payload.userId}`
    );
  } catch (e) {
    throw e;
  }
};

export const getAllContestVotesByUserId = async (
  contestId: string,
  userId: string
) => {
  try {
    if (!(contestId && userId)) {
      return [];
    }
    const response = await axios.get<(ContestVote & { contest_id: string })[]>(
      `${API_ROOT}/api/contest-votes/contests/${contestId}/users/${userId}`
    );
    return response.data || [];
  } catch (error) {
    throw error;
  }
};

export type ContestLeaderboard = {
  activity_id: string;
  contest_id: string;
  user_id: string;
  user_info: string;
  content_link: string;
  content_type: string;
  vote_count: number;
};

export const getLeaderboardByContestId = async (contestId: string) => {
  try {
    if (!contestId) {
      return [];
    }
    const response = await axios.get<ContestLeaderboard[]>(
      `${API_ROOT}/api/contests/${contestId}/leaderboard`
    );
    return response.data || [];
  } catch (error) {
    throw error;
  }
};
